import React from "react"
import { createPortal } from "react-dom"
import { useImageUploader } from "@/hooks/useImageUploader"
import { Loader2Icon } from "lucide-react"
import type { FileWithPath } from "react-dropzone"

export type FileWithPreview = FileWithPath & {
  preview: string
}

// Helper function to determine if a file is PNG format
function isPngFile(file: FileWithPath | null): boolean {
  if (!file) return false;
  
  // Check by MIME type
  if (file.type === 'image/png') return true;
  
  // Check by file extension as fallback
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension === 'png';
}

export type SelectedImageData = {
  fullUrl: string
  previewUrl: string
}

interface ImageEditorProps {
  dialogOpen: boolean
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  initialFile: FileWithPreview | null
  currentImage: SelectedImageData | null
  onEditComplete: (imageData: SelectedImageData) => void
  onCancel: () => void
  targetCropWidth?: number
  targetCropHeight?: number
  allowFreeCrop?: boolean
  outputFormat?: 'png' | 'jpg'
}

// CDN-based Filerobot Editor implementation that renders in document body
function useCDNFilerobotEditor({
  sourceImageSrc,
  onSave,
  onClose,
  isOpen,
  targetCropWidth,
  targetCropHeight,
  allowFreeCrop,
  outputFormat = 'jpg',
}: {
  sourceImageSrc: string
  onSave: (editedBlob: Blob, originalFileName: string) => Promise<void>
  onClose: () => void
  isOpen: boolean
  targetCropWidth?: number
  targetCropHeight?: number
  allowFreeCrop?: boolean
  outputFormat?: 'png' | 'jpg'
}) {
  const editorRef = React.useRef<unknown>(null)
  const initializingStatusRef = React.useRef<"idle" | "loading" | "success" | "error">("idle")
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const closeEditor = React.useCallback(() => {
    const editorContainer = document.getElementById('filerobot-image-editor-container')
    if (editorContainer && document.body.contains(editorContainer)) {
      document.body.removeChild(editorContainer as HTMLDivElement)
    }
    
    // Clean up z-index fix styles
    const styleElement = document.getElementById('filerobot-z-index-fix')
    if (styleElement && document.head.contains(styleElement)) {
      document.head.removeChild(styleElement)
    }
    
    initializingStatusRef.current = "idle"
    onClose()
  }, [onClose])

  React.useEffect(() => {
    if (!isOpen || !sourceImageSrc || initializingStatusRef.current !== "idle") {
      return
    }

    const initializeEditor = async () => {
      if (initializingStatusRef.current !== "idle") {
        return
      }

      initializingStatusRef.current = "loading"
      try {
        setIsLoading(true)
        setError(null)

        // Load JavaScript if not already loaded
        if (!(window as unknown as { FilerobotImageEditor?: unknown }).FilerobotImageEditor) {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script')
            script.src = 'https://scaleflex.cloudimg.io/v7/plugins/filerobot-image-editor/latest/filerobot-image-editor.min.js'
            script.onload = () => resolve()
            script.onerror = (error) => {
              console.error('Failed to load Filerobot editor script:', error)
              initializingStatusRef.current = "error"
              reject(new Error('Failed to load Filerobot editor script'))
            }
            document.head.appendChild(script)
          })
        }

        let editorContainer = document.getElementById('filerobot-image-editor-container')
        if (editorContainer) {
          document.body.removeChild(editorContainer as HTMLDivElement)
        }

        // Create container for the editor
        editorContainer = document.createElement('div')
        editorContainer.id = 'filerobot-image-editor-container'
        editorContainer.style.cssText = `
          width: 100dvw;
          height: 100dvh;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          position: fixed;
          top: 0;
          left: 0;
          z-index: 51;
          pointer-events: auto;
        `
        
        document.body.appendChild(editorContainer)

        // Add CSS to ensure Filerobot poppers appear above our container
        let styleElement = document.getElementById('filerobot-z-index-fix')
        if (!styleElement) {
          styleElement = document.createElement('style')
          styleElement.id = 'filerobot-z-index-fix'
          styleElement.textContent = `
            /* Ensure Filerobot poppers appear above the editor container and can receive events */
            [data-popper-placement],
            [data-popper-placement] *,
            [data-popper-placement] button,
            [data-popper-placement] input,
            [data-popper-placement] .react-colorful {
              z-index: 10000 !important;
              pointer-events: auto !important;
            }
            /* Common popper/dropdown selectors that Filerobot might use */
            .tippy-box,
            .tippy-content,
            .tippy-box *,
            .tippy-content *,
            [role="tooltip"],
            [role="tooltip"] *,
            [role="listbox"],
            [role="listbox"] *,
            [role="menu"],
            [role="menu"] *,
            .dropdown,
            .dropdown *,
            .popover,
            .popover *,
            .react-colorful,
            .react-colorful * {
              z-index: 10000 !important;
              pointer-events: auto !important;
            }
            /* Specific button and interactive element targeting */
            [data-popper-placement] button,
            [role="tooltip"] button,
            [role="listbox"] button,
            [role="menu"] button,
            .tippy-box button,
            .tippy-content button,
            .dropdown button,
            .popover button,
            .react-colorful button,
            .react-colorful input {
              pointer-events: auto !important;
              z-index: 10001 !important;
            }
          `
          document.head.appendChild(styleElement)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const FilerobotImageEditor = (window as any).FilerobotImageEditor
        
        const editorConfig = {
          source: sourceImageSrc,
          tabsIds: [
            FilerobotImageEditor.TABS.ADJUST,
            FilerobotImageEditor.TABS.FILTERS,
            FilerobotImageEditor.TABS.FINETUNE,
            FilerobotImageEditor.TABS.ANNOTATE,
            FilerobotImageEditor.TABS.WATERMARK,
          ],
          defaultTabId: FilerobotImageEditor.TABS.ADJUST,
          defaultToolId: targetCropWidth && targetCropHeight ? FilerobotImageEditor.TOOLS.CROP : undefined,
          defaultSavedImageType: outputFormat,
          closeAfterSave: false,
          onBeforeSave: () => false,
          // Configure crop restrictions
          Crop: {
            ...(targetCropWidth && targetCropHeight && {
              // Set aspect ratio directly (e.g., 1 for square)
              ratio: Math.floor((targetCropWidth / targetCropHeight) * 10) / 10,
              // Hide all presets when we want to enforce specific ratio
              noPresets: !allowFreeCrop,
              // Disable manual resize if not allowing free crop
              disableManualResize: !allowFreeCrop,
            }),
          },
          onSave: async (editedImageObject: { imageCanvas?: HTMLCanvasElement }) => {
            try {
              if (editedImageObject?.imageCanvas) {
                const canvas = editedImageObject.imageCanvas!
                
                // Convert canvas to appropriate format
                const format = outputFormat === 'png' ? 'image/png' : 'image/jpeg'
                const quality = outputFormat === 'png' ? 1.0 : 0.9
                const filename = outputFormat === 'png' ? 'edited-image.png' : 'edited-image.jpg'
                
                const blob = await new Promise<Blob | null>((resolve) => {
                  canvas.toBlob((blob: Blob | null) => {
                    resolve(blob)
                  }, format, quality)
                })

                if (blob) {
                  try {
                    // Call handleSave which handles the upload modal and upload process
                    await onSave(blob, filename)
                    
                    // Close the editor after successful upload
                    closeEditor()
                    
                  } catch (uploadError) {
                    console.error('Upload failed:', uploadError)
                    // Error is already handled in handleSave with modal
                  }
                } else {
                  console.error('Failed to create blob from canvas')
                }
              }
            } catch (err) {
              console.error('Error in onSave callback:', err)
            }
          },
          onClose: () => closeEditor(),
          savingPixelRatio: 2,
          previewPixelRatio: 1,
          showBackButton: true,
          closeButtonProps: {
            style: { zIndex: 10000 }
          },
        }

        const editor = new FilerobotImageEditor(editorContainer, editorConfig)
        editorRef.current = editor
        
        await (editor as { render: () => Promise<void> }).render()

        setIsLoading(false)
        initializingStatusRef.current = "success"
      } catch (error) {
        console.error('Failed to initialize Filerobot editor:', error)
        setError(error instanceof Error ? error.message : 'Failed to load image editor')
        setIsLoading(false)
        
        closeEditor()
        initializingStatusRef.current = "error"
      }
    }

    initializeEditor()
  }, [isOpen, sourceImageSrc, onSave, closeEditor, targetCropWidth, targetCropHeight, allowFreeCrop, outputFormat])

  return { isLoading, error }
}

export function ImageEditor({
  dialogOpen,
  setDialogOpen,
  initialFile,
  currentImage,
  onEditComplete,
  onCancel,
  targetCropWidth = 512,
  targetCropHeight = 512,
  allowFreeCrop = false,
  outputFormat,
}: ImageEditorProps) {
  
  const { uploadImage, isLoading: isUploading, uploadProgress } = useImageUploader()
  
  // Automatically detect PNG format to preserve transparency, fallback to outputFormat prop or jpg
  const effectiveOutputFormat = React.useMemo(() => {
    if (isPngFile(initialFile)) return 'png';
    return outputFormat || 'jpg';
  }, [initialFile, outputFormat]);
  
  // Get the source image - either from initial file or current image for editing
  const sourceImageSrc = React.useMemo(() => {
    if (initialFile?.preview) {
      return initialFile.preview
    }
    if (currentImage?.fullUrl) {
      return currentImage.fullUrl
    }
    return ""
  }, [initialFile, currentImage])

  const handleSave = React.useCallback(async (editedBlob: Blob, originalFileName: string): Promise<void> => {
    try {
      // Use regular image upload for all cases
      await new Promise<void>((resolve, reject) => {
        uploadImage({
          imageBlob: editedBlob,
          originalFileName: originalFileName,
          onSuccess: (result) => {
            onEditComplete({
              fullUrl: result.fullUrl,
              previewUrl: result.fullUrl,
            })
            setDialogOpen(false)
            resolve()
          },
          onError: (error) => {
            console.error('Upload failed:', error)
            reject(error)
          },
        })
      })
    } catch (error) {
      console.error("Error saving image:", error)
      throw new Error("Failed to save image. Please try again.")
    }
  }, [uploadImage, onEditComplete, setDialogOpen])

  const handleClose = React.useCallback(() => {
    try {
      onCancel()
      setDialogOpen(false)
    } catch (error) {
      console.error("Error closing editor:", error)
    }
  }, [onCancel, setDialogOpen])

  const { isLoading: isEditorLoading, error: editorError } = useCDNFilerobotEditor({
    sourceImageSrc,
    onSave: handleSave,
    onClose: handleClose,
    isOpen: dialogOpen && !!sourceImageSrc,
    targetCropWidth,
    targetCropHeight,
    allowFreeCrop,
    outputFormat: effectiveOutputFormat,
  })

  // The actual editor is rendered in document body by useCDNFilerobotEditor
  return (
    <>
      {/* Upload progress overlay - rendered in portal to appear above everything */}
      {isUploading && dialogOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 52 }}
        >
          <div className="bg-white p-6 rounded-lg flex flex-col items-center space-y-4">
            <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
            <p className="text-lg font-medium">Uploading image...</p>
            <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
            <div className="w-48 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error overlay - shown when editor fails to load */}
      {editorError && dialogOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 50000 }}
        >
          <div className="bg-white p-6 rounded-lg text-center max-w-md">
            <p className="text-lg mb-2 text-red-600">Error loading image editor</p>
            <p className="text-sm text-gray-600 mb-4">{editorError}</p>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
      
      {/* Loading overlay - shown while editor is initializing */}
      {isEditorLoading && dialogOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 50000 }}
        >
          <div className="bg-white p-6 rounded-lg text-center">
            <Loader2Icon className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading image editor...</p>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}